// frontend/src/pages/DailyMirrorAffirmationPage.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, CheckCircle2, Download, Share2, Sparkles, X, Square } from "lucide-react";

import { useSelfieCamera } from "../hooks/useSelfieCamera";
import {
  DAILY_AFFIRMATIONS,
  DAILY_MIRROR_CYCLE_LENGTH
} from "../content/dailyMirror";
import {
  daysBetweenISO,
  getOrInitStartDateISO,
  isCompletedToday,
  markCompletedToday,
  todayISO
} from "../utils/dailyMirror";

/**
 * Daily Mirror — mobile-first selfie experience
 * - Clean Mirror Mode (default): top nav + affirmation + small "tap to show controls" hint
 * - Controls Mode: frosted bottom dock with actions; auto-hides after 3s
 * - Branded Save: Canvas-based export (selfie + affirmation + day + UnityLove brand)
 */
export default function DailyMirrorAffirmationPage() {
  const navigate = useNavigate();
  const {
    videoRef,
    isCameraOn,
    isRequesting,
    error,
    startStream,
    stopStream
  } = useSelfieCamera();

  const [completed, setCompleted] = useState(isCompletedToday());
  const [controlsVisible, setControlsVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const hideTimerRef = useRef(null);

  const handleExitToDashboard = () => navigate("/dashboard");
  const handleSmartBack = () => {
    if (window.history.length > 1) navigate(-1);
    else handleExitToDashboard();
  };

  const dayIndex = useMemo(() => {
    const startISO = getOrInitStartDateISO();
    const idx = daysBetweenISO(startISO, todayISO());
    return (
      ((idx % DAILY_MIRROR_CYCLE_LENGTH) + DAILY_MIRROR_CYCLE_LENGTH) %
      DAILY_MIRROR_CYCLE_LENGTH
    );
  }, []);

  const affirmation = useMemo(() => {
    return DAILY_AFFIRMATIONS[dayIndex] || DAILY_AFFIRMATIONS[0];
  }, [dayIndex]);

  const handleComplete = () => {
    markCompletedToday();
    setCompleted(true);
  };

  // ---------- Branded export (Canvas API) ----------
  const wrapText = (ctx, text, maxWidth) => {
    const words = text.split(" ");
    const lines = [];
    let line = "";
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (ctx.measureText(test).width > maxWidth && line) {
        lines.push(line);
        line = w;
      } else {
        line = test;
      }
    }
    if (line) lines.push(line);
    return lines;
  };

  const handleExport = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      const canvas = await buildBrandedCanvas();
      const link = document.createElement("a");
      link.download = `unitylove-daily-mirror-day-${dayIndex + 1}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setSaving(false);
    }
  // eslint-disable-next-line
  }, [saving, isCameraOn, dayIndex, affirmation]);

  const handleShare = useCallback(async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const canvas = await buildBrandedCanvas();
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.95));
      if (!blob) throw new Error("Could not generate image");

      const file = new File([blob], `unitylove-day-${dayIndex + 1}.png`, { type: "image/png" });
      const shareData = {
        files: [file],
        title: "Daily Mirror — UnityLove",
        text: `${affirmation}\n\n— Daily Mirror · joinunitylove.com`
      };

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share(shareData);
      } else {
        // Fallback: download + copy text to clipboard for manual paste
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = file.name;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
        try {
          await navigator.clipboard?.writeText(shareData.text);
        } catch (_) {}
      }
    } catch (err) {
      if (err?.name !== "AbortError") console.error("Share failed:", err);
    } finally {
      setSharing(false);
    }
  // eslint-disable-next-line
  }, [sharing, isCameraOn, dayIndex, affirmation]);

  // Build the branded 1080x1920 canvas (used by both Save & Share)
  const buildBrandedCanvas = useCallback(async () => {
      const W = 1080;
      const H = 1920;
      const canvas = document.createElement("canvas");
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext("2d");

      // Base background
      ctx.fillStyle = "#0a0a0a";
      ctx.fillRect(0, 0, W, H);

      // Draw selfie (mirrored to match user's preview), object-cover
      const v = videoRef.current;
      if (v && isCameraOn && v.videoWidth > 0 && v.videoHeight > 0) {
        const vw = v.videoWidth;
        const vh = v.videoHeight;
        const scale = Math.max(W / vw, H / vh);
        const dw = vw * scale;
        const dh = vh * scale;
        const dx = (W - dw) / 2;
        const dy = (H - dh) / 2;
        ctx.save();
        ctx.translate(W, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(v, W - dx - dw, dy, dw, dh);
        ctx.restore();
      } else {
        const g = ctx.createLinearGradient(0, 0, 0, H);
        g.addColorStop(0, "#1a0510");
        g.addColorStop(0.5, "#2a0a18");
        g.addColorStop(1, "#0a0306");
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, W, H);
      }

      // Top dark gradient
      const topG = ctx.createLinearGradient(0, 0, 0, H * 0.55);
      topG.addColorStop(0, "rgba(0,0,0,0.78)");
      topG.addColorStop(0.6, "rgba(0,0,0,0.35)");
      topG.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = topG;
      ctx.fillRect(0, 0, W, H * 0.55);

      // Bottom dark gradient
      const bottomG = ctx.createLinearGradient(0, H * 0.7, 0, H);
      bottomG.addColorStop(0, "rgba(0,0,0,0)");
      bottomG.addColorStop(1, "rgba(0,0,0,0.85)");
      ctx.fillStyle = bottomG;
      ctx.fillRect(0, H * 0.7, W, H * 0.3);

      // Day pill (top-center)
      ctx.font = "600 36px 'Inter', system-ui, -apple-system, sans-serif";
      const dayText = `Day ${dayIndex + 1} / ${DAILY_MIRROR_CYCLE_LENGTH}`;
      const dayW = ctx.measureText(dayText).width;
      const pillPadX = 40;
      const pillH = 78;
      const pillW = dayW + pillPadX * 2;
      const pillX = (W - pillW) / 2;
      const pillY = 110;
      ctx.fillStyle = "rgba(255,255,255,0.12)";
      ctx.beginPath();
      ctx.roundRect(pillX, pillY, pillW, pillH, pillH / 2);
      ctx.fill();
      ctx.strokeStyle = "rgba(255,255,255,0.18)";
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.fillStyle = "rgba(255,255,255,0.92)";
      ctx.textBaseline = "middle";
      ctx.fillText(dayText, pillX + pillPadX, pillY + pillH / 2);

      // Affirmation text
      ctx.font = "700 64px 'Inter', system-ui, -apple-system, sans-serif";
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      const maxTextW = W - 160;
      const lines = wrapText(ctx, affirmation, maxTextW);
      const lineH = 84;
      const blockH = lines.length * lineH;
      const startY = H * 0.34 - blockH / 2;
      ctx.shadowColor = "rgba(0,0,0,0.65)";
      ctx.shadowBlur = 24;
      ctx.shadowOffsetY = 4;
      lines.forEach((ln, i) => {
        ctx.fillText(ln, W / 2, startY + i * lineH + lineH / 2);
      });
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;

      // Brand mark (bottom-right)
      ctx.textAlign = "right";
      ctx.textBaseline = "alphabetic";
      const drawHeart = (cx, cy, size, fill) => {
        ctx.fillStyle = fill;
        ctx.beginPath();
        const s = size;
        ctx.moveTo(cx, cy + s * 0.25);
        ctx.bezierCurveTo(cx, cy, cx - s, cy, cx - s, cy + s * 0.35);
        ctx.bezierCurveTo(cx - s, cy + s * 0.7, cx, cy + s * 0.95, cx, cy + s * 1.2);
        ctx.bezierCurveTo(cx, cy + s * 0.95, cx + s, cy + s * 0.7, cx + s, cy + s * 0.35);
        ctx.bezierCurveTo(cx + s, cy, cx, cy, cx, cy + s * 0.25);
        ctx.closePath();
        ctx.fill();
      };

      ctx.font = "600 32px 'Inter', system-ui, -apple-system, sans-serif";
      const brandText = "UnityLove";
      const brandW = ctx.measureText(brandText).width;
      const brandRightX = W - 60;
      const brandY = H - 90;
      drawHeart(brandRightX - brandW - 18, brandY - 22, 14, "#FF4F7A");

      ctx.font = "600 32px 'Inter', system-ui, -apple-system, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.95)";
      ctx.fillText(brandText, brandRightX, brandY);

      ctx.font = "500 24px 'Inter', system-ui, -apple-system, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.65)";
      ctx.fillText("joinunitylove.com", brandRightX, brandY + 36);

      return canvas;
  // eslint-disable-next-line
  }, [videoRef, isCameraOn, dayIndex, affirmation]);

  // ---------- Auto-hide controls ----------
  const scheduleHide = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), 3000);
  }, []);

  const toggleControls = useCallback(() => {
    try { if (navigator.vibrate) navigator.vibrate(10); } catch (_) {}
    setControlsVisible(prev => {
      if (!prev) {
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        hideTimerRef.current = setTimeout(() => setControlsVisible(false), 3000);
        return true;
      }
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      return false;
    });
  }, []);

  const bumpTimer = useCallback(() => {
    if (controlsVisible) scheduleHide();
  }, [controlsVisible, scheduleHide]);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Full-viewport mirror — controls live in their own dock */}
      <div
        className="relative overflow-hidden bg-black select-none"
        style={{ minHeight: "100dvh" }}
        onClick={toggleControls}
        data-testid="daily-mirror-stage"
      >
        {/* Camera feed (mirrored selfie) */}
        <div className="absolute inset-0">
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            className="h-full w-full object-cover -scale-x-100"
          />
        </div>

        {/* Soft cinematic vignette — never covers face area */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-black/65 via-black/25 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-black/75 via-black/30 to-transparent" />

        {/* ===== TOP NAV (always visible) ===== */}
        <div className="absolute left-0 right-0 top-0 z-20 px-4 pt-[max(env(safe-area-inset-top),16px)]">
          <div className="flex items-center justify-between">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-full bg-black/45 p-2.5 text-white/90 backdrop-blur-md hover:bg-black/55 transition active:scale-95"
              onClick={(e) => { e.stopPropagation(); handleSmartBack(); }}
              aria-label="Back"
              data-testid="mirror-back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="rounded-full bg-black/40 px-3.5 py-1.5 text-xs font-medium tracking-wide text-white/85 backdrop-blur-md">
              Day {dayIndex + 1} / {DAILY_MIRROR_CYCLE_LENGTH}
            </div>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-full bg-black/45 p-2.5 text-white/90 backdrop-blur-md hover:bg-black/55 transition active:scale-95"
              onClick={(e) => { e.stopPropagation(); handleExitToDashboard(); }}
              aria-label="Exit"
              data-testid="mirror-exit-btn"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* ===== AFFIRMATION (upper-third, never covers face) ===== */}
        <div className="pointer-events-none absolute inset-x-0 top-[22%] px-8 text-center z-10">
          <p className="text-xl sm:text-2xl md:text-3xl font-semibold leading-snug text-white drop-shadow-[0_2px_14px_rgba(0,0,0,0.75)] max-w-xl mx-auto">
            {affirmation}
          </p>
          {completed && (
            <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/20 border border-emerald-300/25 px-3 py-1 text-[11px] font-medium text-emerald-200 backdrop-blur-md">
              <CheckCircle2 className="h-3 w-3" />
              Completed today
            </div>
          )}
        </div>

        {/* "Start Mirror" centered CTA when camera is off */}
        {!isCameraOn && !isRequesting && (
          <div className="absolute inset-x-0 top-[55%] -translate-y-1/2 z-10 px-6 text-center">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); startStream(); }}
              className="inline-flex items-center gap-2 rounded-full bg-rose-500/90 hover:bg-rose-500 px-6 py-3 text-white font-medium shadow-[0_8px_30px_rgba(255,79,122,0.4)] backdrop-blur-md transition active:scale-95"
              data-testid="mirror-start-cta"
            >
              <Camera className="h-5 w-5" />
              Start Mirror
            </button>
            <p className="mt-3 text-xs text-white/55">
              Camera is local-only. We never record or upload.
            </p>
          </div>
        )}

        {isRequesting && (
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-10 text-center text-white/70 text-sm">
            Requesting camera…
          </div>
        )}

        {/* ===== SUBTLE BRAND MARK (bottom-right, always visible) ===== */}
        <div className="pointer-events-none absolute right-4 bottom-[max(env(safe-area-inset-bottom),16px)] z-10 text-right">
          <div className="flex items-center justify-end gap-1.5 text-white/85">
            <span className="text-rose-400 text-sm leading-none">♥</span>
            <span className="text-sm font-semibold tracking-tight">UnityLove</span>
          </div>
          <div className="text-[10px] text-white/50 tracking-wide">joinunitylove.com</div>
        </div>

        {/* ===== TAP HINT (when controls hidden) ===== */}
        <div
          className={[
            "pointer-events-none absolute inset-x-0 bottom-[max(env(safe-area-inset-bottom),20px)] z-10 transition-opacity duration-300 text-center",
            controlsVisible ? "opacity-0" : "opacity-100"
          ].join(" ")}
        >
          <span className="text-[11px] text-white/45 tracking-wide">Tap to show controls</span>
        </div>

        {/* ===== BOTTOM CONTROLS DOCK (frosted, slides up) ===== */}
        <div
          className={[
            "absolute inset-x-0 bottom-0 z-30 transition-all duration-300 ease-out",
            controlsVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-6 pointer-events-none"
          ].join(" ")}
          data-testid="mirror-controls-dock"
        >
          <div
            className="mx-auto w-full max-w-md px-4 pb-[max(env(safe-area-inset-bottom),20px)] pt-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rounded-2xl border border-white/15 bg-black/55 backdrop-blur-xl shadow-[0_10px_40px_rgba(0,0,0,0.4)]">
              <div className="flex items-center justify-between gap-1 px-2 py-3">
                <DockButton
                  label={completed ? "Done" : "Complete"}
                  icon={<CheckCircle2 className="h-5 w-5" />}
                  active={completed}
                  onClick={() => { handleComplete(); bumpTimer(); }}
                  testId="mirror-complete-btn"
                />
                {!isCameraOn ? (
                  <DockButton
                    label="Mirror"
                    icon={<Camera className="h-5 w-5" />}
                    disabled={isRequesting}
                    onClick={() => { startStream(); bumpTimer(); }}
                    testId="mirror-camera-on-btn"
                  />
                ) : (
                  <DockButton
                    label="Stop"
                    icon={<Square className="h-5 w-5" />}
                    onClick={() => { stopStream(); bumpTimer(); }}
                    testId="mirror-camera-off-btn"
                  />
                )}
                <DockButton
                  label="Prompts"
                  icon={<Sparkles className="h-5 w-5" />}
                  onClick={() => { navigate("/mirror-prompts"); }}
                  testId="mirror-prompts-btn"
                />
                <DockButton
                  label={saving ? "Saving" : "Save"}
                  icon={<Download className="h-5 w-5" />}
                  disabled={saving}
                  onClick={() => { handleExport(); bumpTimer(); }}
                  testId="mirror-screenshot-btn"
                />
                <DockButton
                  label={sharing ? "..." : "Share"}
                  icon={<Share2 className="h-5 w-5" />}
                  disabled={sharing}
                  onClick={() => { handleShare(); bumpTimer(); }}
                  testId="mirror-share-btn"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Camera error */}
        {error && (
          <div className="absolute inset-x-0 bottom-28 px-6 z-10">
            <div className="mx-auto max-w-sm rounded-xl border border-rose-200/20 bg-rose-500/15 px-3 py-2 text-sm text-rose-200 backdrop-blur text-center">
              Camera unavailable: {error}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DockButton({ label, icon, onClick, active, disabled, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={[
        "flex flex-1 flex-col items-center justify-center gap-1 rounded-xl px-2 py-2 text-[11px] font-medium transition active:scale-95",
        active
          ? "bg-emerald-500/25 text-emerald-200 border border-emerald-300/25"
          : "text-white/90 hover:bg-white/10 border border-transparent",
        disabled ? "opacity-50" : ""
      ].join(" ")}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
